import { collectImportBlockElements } from "../../docxFidelity/merge/collectBlocks";
import { numFmtToListStyleType } from "../../docxFidelity/ooxml/numberingCore";
import {
  type ListRun,
  normalizedLeadingText,
  parseLeadingCounter,
  structureListRuns,
  stripLeadingListMarkerFromElement,
} from "./structureListRuns";
import type { MsoListMeta, WordPasteStats } from "./types";

const MANUAL_DECIMAL_RE = /^\d+[.)]\s+/;
const MANUAL_LOWER_ALPHA_RE = /^\([a-z]+\)\s+/i;
const MANUAL_LOWER_ROMAN_RE = /^\([ivxlcdm]+\)\s+/i;
const MANUAL_UPPER_ALPHA_RE = /^[A-Z][.)]\s+/;

function inferNumFmtFromText(text: string): string | null {
  if (MANUAL_DECIMAL_RE.test(text)) return "decimal";
  if (MANUAL_LOWER_ALPHA_RE.test(text)) return "lowerletter";
  if (MANUAL_LOWER_ROMAN_RE.test(text)) return "lowerroman";
  if (MANUAL_UPPER_ALPHA_RE.test(text)) return "upperletter";
  return null;
}

function inferListStyleFromText(text: string): string | null {
  return numFmtToListStyleType(inferNumFmtFromText(text));
}

function parseManualCounter(text: string, numFmt: string | null): number | null {
  if (numFmt === "decimal") return parseLeadingCounter(text);
  const alphaMatch = text.match(/^\(([a-z]+)\)/i);
  if (alphaMatch) {
    return alphaMatch[1].toLowerCase().charCodeAt(0) - 96;
  }
  const romanMatch = text.match(/^\(([ivxlcdm]+)\)/i);
  if (romanMatch) {
    const roman = romanMatch[1].toLowerCase();
    const values: Record<string, number> = {
      i: 1, ii: 2, iii: 3, iv: 4, v: 5, vi: 6, vii: 7, viii: 8, ix: 9, x: 10,
    };
    return values[roman] ?? null;
  }
  const upperMatch = text.match(/^([A-Z])[.)]/);
  if (upperMatch) {
    return upperMatch[1].charCodeAt(0) - 64;
  }
  return null;
}

function inferIlvlFromIndent(el: HTMLElement): number {
  const marginLeft = parseInt(el.style.marginLeft || "0", 10) || 0;
  const textIndent = parseInt(el.style.textIndent || "0", 10) || 0;
  const total = Math.max(marginLeft, marginLeft + textIndent);
  if (total <= 0) return 0;
  return Math.max(1, Math.round(total / 48));
}

function shouldStartNewMsoInstance(
  currentRun: ListRun | null,
  meta: MsoListMeta,
  visibleCounter: number | null,
): boolean {
  if (!currentRun) return true;
  if (meta.ilvl > 0) return false;
  if (currentRun.ilvl > meta.ilvl) return false;
  if (currentRun.lfo != null && meta.lfo != null && currentRun.lfo !== meta.lfo) {
    return true;
  }
  if (
    visibleCounter != null &&
    currentRun.lastCounter != null &&
    meta.ilvl === currentRun.ilvl &&
    visibleCounter < currentRun.lastCounter
  ) {
    return true;
  }
  if (
    visibleCounter === 1 &&
    currentRun.lastCounter != null &&
    currentRun.lastCounter > 1 &&
    meta.ilvl === currentRun.ilvl
  ) {
    return true;
  }
  return false;
}

function shouldStartNewManualInstance(
  currentRun: ListRun | null,
  ilvl: number,
  listStyleType: string | null,
  visibleCounter: number | null,
): boolean {
  if (!currentRun) return ilvl === 0;
  if (ilvl > 0) return false;
  if (currentRun.ilvl > ilvl) return false;
  if (currentRun.ilvl !== ilvl || currentRun.listStyleType !== listStyleType) {
    return ilvl === 0;
  }
  if (
    visibleCounter != null &&
    currentRun.lastCounter != null &&
    visibleCounter < currentRun.lastCounter
  ) {
    return true;
  }
  if (
    visibleCounter === 1 &&
    currentRun.lastCounter != null &&
    currentRun.lastCounter > 1
  ) {
    return true;
  }
  return false;
}

function buildRunsFromMso(
  blocks: HTMLElement[],
  msoMeta: Map<HTMLElement, MsoListMeta>,
): { runs: ListRun[]; stats: Pick<WordPasteStats, "msoListParagraphs" | "multilevelApplied"> } {
  const runs: ListRun[] = [];
  let currentRun: ListRun | null = null;
  let msoListParagraphs = 0;
  let multilevelApplied = 0;

  for (const el of blocks) {
    const meta = msoMeta.get(el);
    if (!meta) {
      currentRun = null;
      continue;
    }

    msoListParagraphs++;
    if (meta.ilvl > 0) multilevelApplied++;

    const listStyleType = numFmtToListStyleType(meta.numFmt);
    const visibleCounter = meta.visibleLabel
      ? parseManualCounter(meta.visibleLabel, meta.numFmt ?? "decimal")
      : null;
    const startValue = meta.visibleLabel
      ? parseLeadingCounter(meta.visibleLabel)
      : null;

    const sameRun =
      currentRun &&
      currentRun.listId === meta.listId &&
      currentRun.ilvl === meta.ilvl &&
      currentRun.lfo === meta.lfo &&
      !shouldStartNewMsoInstance(currentRun, meta, visibleCounter);

    if (sameRun) {
      currentRun!.elements.push(el);
      if (visibleCounter != null) {
        currentRun!.lastCounter = visibleCounter;
      }
    } else {
      const isNewInstance = shouldStartNewMsoInstance(currentRun, meta, visibleCounter);
      currentRun = {
        listId: meta.listId,
        ilvl: meta.ilvl,
        lfo: meta.lfo,
        numFmt: meta.numFmt,
        elements: [el],
        startValue,
        listStyleType,
        isNewInstance,
        lastCounter: visibleCounter,
      };
      runs.push(currentRun);
    }
  }

  return { runs, stats: { msoListParagraphs, multilevelApplied } };
}

function buildRunsFromManualPrefixes(blocks: HTMLElement[]): ListRun[] {
  const runs: ListRun[] = [];
  let currentRun: ListRun | null = null;

  for (const el of blocks) {
    if (el.closest("ol, ul, table")) {
      currentRun = null;
      continue;
    }

    const text = normalizedLeadingText(el);
    const numFmt = text ? inferNumFmtFromText(text) : null;
    if (!numFmt) {
      currentRun = null;
      continue;
    }

    const listStyleType = inferListStyleFromText(text);
    const ilvl = inferIlvlFromIndent(el);
    const listId = listStyleType ? listStyleType.charCodeAt(0) + ilvl : ilvl;
    const visibleCounter = parseManualCounter(text, numFmt);

    const sameRun =
      currentRun &&
      currentRun.listStyleType === listStyleType &&
      currentRun.ilvl === ilvl &&
      !shouldStartNewManualInstance(currentRun, ilvl, listStyleType, visibleCounter);

    if (sameRun) {
      currentRun!.elements.push(el);
      if (visibleCounter != null) {
        currentRun!.lastCounter = visibleCounter;
      }
    } else {
      const isNewInstance = shouldStartNewManualInstance(
        currentRun,
        ilvl,
        listStyleType,
        visibleCounter,
      );
      currentRun = {
        listId,
        ilvl,
        lfo: null,
        numFmt,
        elements: [el],
        startValue: parseLeadingCounter(text),
        listStyleType,
        isNewInstance,
        lastCounter: visibleCounter,
      };
      runs.push(currentRun);
    }
  }

  return runs;
}

/**
 * Convert Word pseudo-list paragraphs into editable ol/ul structures.
 */
export function structureLegalNumbering(
  root: ParentNode,
  msoMeta: Map<HTMLElement, MsoListMeta>,
): WordPasteStats {
  const stats: WordPasteStats = {
    msoListParagraphs: 0,
    listsStructured: 0,
    multilevelApplied: 0,
  };

  const blocks = collectImportBlockElements(root);
  const msoResult = buildRunsFromMso(blocks, msoMeta);
  stats.msoListParagraphs = msoResult.stats.msoListParagraphs;
  stats.multilevelApplied = msoResult.stats.multilevelApplied;

  const doc = root.ownerDocument ?? document;
  if (msoResult.runs.length > 0) {
    stats.listsStructured += structureListRuns(doc, msoResult.runs);
    return stats;
  }

  const manualRuns = buildRunsFromManualPrefixes(blocks);
  stats.listsStructured += structureListRuns(doc, manualRuns);

  return stats;
}
